import type { MapLocation } from '@/types';

interface GeolocationPosition extends MapLocation {
  accuracy: number;
  timestamp: number;
}

interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

type PermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

class GeolocationService {
  private watchId: number | null = null;
  private lastKnownPosition: GeolocationPosition | null = null;

  /**
   * Get current position
   */
  async getCurrentPosition(options?: GeolocationOptions): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      const defaultOptions: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
        ...options,
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const result: GeolocationPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          };

          this.lastKnownPosition = result;
          resolve(result);
        },
        (error) => {
          let message = 'Error getting location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location permission denied';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Location unavailable';
              break;
            case error.TIMEOUT:
              message = 'Location timeout';
              break;
          }
          reject(new Error(message));
        },
        defaultOptions
      );
    });
  }

  /**
   * Check geolocation permission status
   */
  async checkPermission(): Promise<PermissionState> {
    if (!navigator.permissions) return 'unsupported';

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state as PermissionState;
    } catch (error) {
      return 'unsupported';
    }
  }

  /**
   * Watch position changes
   */
  watchPosition(
    onSuccess: (position: GeolocationPosition) => void,
    onError?: (error: Error) => void,
    options?: GeolocationOptions
  ): number | null {
    if (!navigator.geolocation) {
      onError?.(new Error('Geolocation not supported'));
      return null;
    }

    const defaultOptions: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000,
      ...options,
    };

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const result: GeolocationPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        };

        this.lastKnownPosition = result;
        onSuccess(result);
      },
      (error) => {
        let message = 'Error watching location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Location unavailable';
            break;
          case error.TIMEOUT:
            message = 'Location timeout';
            break;
        }
        onError?.(new Error(message));
      },
      defaultOptions
    );

    return this.watchId;
  }

  /**
   * Stop watching position
   */
  clearWatch(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  /**
   * Get last known position
   */
  getLastKnownPosition(): GeolocationPosition | null {
    return this.lastKnownPosition;
  }

  /**
   * Calculate distance between two points using Haversine formula
   */
  calculateDistance(
    point1: MapLocation,
    point2: MapLocation
  ): number {
    const R = 6371; // Earth's radius in km
    const dLat = (point2.lat - point1.lat) * Math.PI / 180;
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(point1.lat * Math.PI / 180) *
        Math.cos(point2.lat * Math.PI / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Calculate bearing between two points
   */
  calculateBearing(
    point1: MapLocation,
    point2: MapLocation
  ): number {
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;
    const lat1 = point1.lat * Math.PI / 180;
    const lat2 = point2.lat * Math.PI / 180;

    const y = Math.sin(dLng) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

    const bearing = Math.atan2(y, x) * 180 / Math.PI;
    return (bearing + 360) % 360; // Normalize to 0-360
  }

  /**
   * Check if a point is within a certain radius of another point
   */
  isWithinRadius(
    center: MapLocation,
    point: MapLocation,
    radiusKm: number
  ): boolean {
    const distance = this.calculateDistance(center, point);
    return distance <= radiusKm;
  }

  /**
   * Get points within radius of a center point
   */
  getPointsWithinRadius<T extends MapLocation>(
    center: MapLocation,
    points: T[],
    radiusKm: number
  ): T[] {
    return points.filter(point => this.isWithinRadius(center, point, radiusKm));
  }

  /**
   * Find nearest point to a given location
   */
  findNearestPoint<T extends MapLocation>(
    location: MapLocation,
    points: T[]
  ): { point: T; distance: number } | null {
    if (points.length === 0) return null;

    let nearestPoint = points[0];
    let minDistance = this.calculateDistance(location, nearestPoint);

    for (let i = 1; i < points.length; i++) {
      const distance = this.calculateDistance(location, points[i]);
      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = points[i];
      }
    }

    return { point: nearestPoint, distance: minDistance };
  }

  /**
   * Format coordinates for display
   */
  formatCoordinates(
    location: MapLocation,
    precision: number = 6
  ): string {
    const lat = location.lat.toFixed(precision);
    const lng = location.lng.toFixed(precision);
    return `${lat}, ${lng}`;
  }

  /**
   * Convert coordinates to DMS (Degrees, Minutes, Seconds) format
   */
  toDMS(location: MapLocation): {
    lat: string;
    lng: string;
  } {
    const convertToDMS = (coordinate: number, isLatitude: boolean): string => {
      const absolute = Math.abs(coordinate);
      const degrees = Math.floor(absolute);
      const minutes = Math.floor((absolute - degrees) * 60);
      const seconds = ((absolute - degrees - minutes / 60) * 3600).toFixed(2);
      
      const direction = isLatitude
        ? coordinate >= 0 ? 'N' : 'S'
        : coordinate >= 0 ? 'E' : 'W';
      
      return `${degrees}°${minutes}'${seconds}"${direction}`;
    };

    return {
      lat: convertToDMS(location.lat, true),
      lng: convertToDMS(location.lng, false),
    };
  }

  /**
   * Check if geolocation is available
   */
  isAvailable(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Request permission for geolocation
   */
  async requestPermission(): Promise<PermissionState> {
    if (!this.isAvailable()) {
      return 'unsupported';
    }

    try {
      // Try to get current position to trigger permission request
      await this.getCurrentPosition({ timeout: 5000 });
      return 'granted';
    } catch (error) {
      const permission = await this.checkPermission();
      return permission;
    }
  }
}

export const geolocationService = new GeolocationService();
export default geolocationService;
